import * as React from 'react';
import { useInteractOutside, usePreventScroll } from 'react-aria';
import { type StyleProp, TouchableWithoutFeedback, type View, type ViewStyle } from 'react-native';
import type { SetterOrUpdater } from 'recoil';

import { Button } from '@gitbook/spine-react';
import { useMergedRefs } from '@gitbook/util-react-hooks';

import { animations, useAnimateCSSLifecycle, useAnimateLifecycle } from '../../Animate';
import { BackgroundView } from '../../BackgroundView';
import { spacing } from '../../constants';
import type { TextInputRef } from '../../Form';
import type { PopoverPlacement } from '../../Popover';
import { PortalAbsolute, type PortalAbsoluteRef, useCloseable } from '../../Portal';
import { Responsive } from '../../Responsive';
import { ThemedStyleSheet, useCurrentTheme } from '../../theme';
import { hasPaletteSearchInput } from '../transforms';
import type { PaletteButtonDef, PaletteDef, VisiblePalette } from '../types';
import { PaletteCommandsList } from './PaletteCommandsList';
import { PaletteErrorMessage } from './PaletteErrorMessage';
import { PaletteQueryInput } from './PaletteQueryInput';
import { useComputePaletteCommands } from './useComputePaletteCommands';
import { usePalettePosition } from './usePalettePosition';

const ResponsiveBackgroundView = Responsive.createResponsiveComponent(BackgroundView);

interface PaletteViewProps {
    paletteState: VisiblePalette;
    setPaletteState: SetterOrUpdater<VisiblePalette | null>;
}

/**
 * Component to render a specific palette.
 */
export function PaletteView(props: PaletteViewProps): React.ReactElement {
    const {
        paletteState: { palette, anchorRef, inputRef },
        setPaletteState,
    } = props;

    const portalRef = React.useRef<PortalAbsoluteRef>(null);

    const close = () => setPaletteState(null);

    const animatedBackdropRef = useAnimateCSSLifecycle<HTMLDivElement>({
        mount: 'animate-in fade-in',
        unmount: 'animate-out fade-out',
    });

    // when the palette is controlled, we still want to close it when pressing escape
    useCloseable(
        {
            opened: true,
            closeOnEsc: !hasPaletteSearchInput(palette),
            closeOnClick: false,
            closeOnOutsideClick: false,
            closeOnScroll: false,
            onClose: close,
        },
        portalRef
    );
    usePreventScroll();

    const ref = React.useRef<HTMLDivElement>(null);
    useInteractOutside({
        ref,
        onInteractOutside: (event) => {
            // If we interact with the input linked to the palette, do not close.
            if (event.target === inputRef?.current) {
                return;
            }

            // If we interact with the palette anchor, do not close.
            if (
                event.target instanceof Element &&
                anchorRef.current instanceof Element &&
                anchorRef.current.contains(event.target)
            ) {
                return;
            }

            close();
        },
    });

    /** Since the palette is rendered inside a portal, when the palette is used inside
     * another portal (ie: Modal) we need to set its portal to be its parent portal container
     * so events (ie: right clicks) are not considered "outside" the parent portal which would close it.
     */
    const container = React.useMemo(
        () => findNearestLayerContainer((anchorRef?.current as HTMLDivElement) ?? null),
        [anchorRef?.current]
    );

    return (
        <PortalAbsolute ref={portalRef} container={container}>
            <div
                ref={animatedBackdropRef}
                data-palette-overlay="true"
                className="pointer-events-none fixed inset-0 z-[--z-index-popover] flex size-full items-start justify-center"
            >
                <div className="pointer-events-auto flex" ref={ref} data-testid="palette">
                    <PositionedPalette
                        setPaletteState={props.setPaletteState}
                        setTopPaletteState={props.setPaletteState}
                        paletteState={props.paletteState}
                    />
                </div>
            </div>
        </PortalAbsolute>
    );
}

/**
 * Return the closest portal parent container element.
 */
function findNearestLayerContainer(anchorEl: HTMLElement | null): HTMLElement | null {
    return (
        anchorEl?.closest('[data-spine-popover], [data-spine-modal], [data-spine-sidebar]') ?? null
    );
}

/**
 * Component to render the palette in a specific position.
 */
function PositionedPalette(
    props: PaletteViewProps & {
        /**
         * Placement of the parent palette relative to its own parent/anchor.
         * We want to prioritize positioning in the same direction the descendants
         * to avoid alternating between left and right.
         */
        parentPlacement?: PopoverPlacement;

        /**
         * Callback to update the top-level palette.
         * Constant across all descendants.
         */
        setTopPaletteState: SetterOrUpdater<VisiblePalette | null>;
    }
): React.ReactElement {
    const {
        paletteState: { palette, anchorRef },
        setPaletteState,
        setTopPaletteState,
        parentPlacement,
    } = props;

    // Track whether the animation of the top-level palette has finished, only then the palette will become interactive. Prevents descendant palettes from opening prematurely.
    const isTopLevel = !palette.parent;
    const [isAnimatingIn, setIsAnimatingIn] = React.useState(isTopLevel);

    const popoverRef = React.useRef<View>(null);
    const [positionStyle, placement] = usePalettePosition({
        anchorRef,
        popoverRef,
        palette,
        parentPlacement,
        preferredPlacement: palette.position?.preferredPlacement ?? parentPlacement,
    });

    const animatedPaletteRef = useAnimateLifecycle(animations.slideToL[placement], {
        onEnd: () => {
            setIsAnimatingIn(false);
        },
    });
    const contentRef = useMergedRefs(popoverRef, animatedPaletteRef);

    const setPalette: SetterOrUpdater<PaletteDef> = (update) => {
        setPaletteState((current) => {
            if (!current) {
                return null;
            }

            const newPalette = typeof update === 'function' ? update(current.palette) : update;
            return newPalette
                ? {
                      ...current,
                      palette: newPalette,
                  }
                : null;
        });
    };

    const setTopPalette: SetterOrUpdater<PaletteDef> = (update) => {
        setTopPaletteState((current) => {
            if (!current) {
                return null;
            }

            const newPalette = typeof update === 'function' ? update(current.palette) : update;
            return newPalette
                ? {
                      ...current,
                      palette: newPalette,
                  }
                : null;
        });
    };

    const { descendant } = palette;

    return (
        <>
            <PaletteContent
                ref={contentRef}
                palette={palette}
                setPalette={setPalette}
                setTopPalette={setTopPalette}
                positionStyle={positionStyle}
                placement={placement}
                isAnimatingIn={isAnimatingIn}
            />

            {descendant ? (
                <PositionedPalette
                    paletteState={descendant}
                    setPaletteState={(update) => {
                        setPaletteState((current) => {
                            if (!current) {
                                return null;
                            }

                            const nextDescendantPalette =
                                typeof update === 'function'
                                    ? update(current.palette.descendant ?? null)
                                    : update;

                            return {
                                ...current,
                                palette: {
                                    ...current.palette,
                                    descendant: nextDescendantPalette ?? undefined,
                                },
                            };
                        });
                    }}
                    setTopPaletteState={setTopPaletteState}
                    parentPlacement={placement}
                />
            ) : null}
        </>
    );
}

/**
 * Component to render the palette content.
 */
const PaletteContent = React.forwardRef(function PaletteContentWithRef(
    props: {
        palette: PaletteDef;
        setPalette: SetterOrUpdater<PaletteDef | null>;
        setTopPalette: SetterOrUpdater<PaletteDef>;
        positionStyle: StyleProp<ViewStyle>;
        placement: PopoverPlacement;
        isAnimatingIn: boolean;
    },
    ref: React.Ref<View>
): React.ReactElement {
    const { palette, setPalette, setTopPalette, positionStyle, placement, isAnimatingIn } = props;
    const computed = useComputePaletteCommands(palette);

    const firstButtonRef = React.useRef<HTMLButtonElement>(null);
    const inputRef = React.useRef<TextInputRef>(null);
    const [theme, styles] = useCurrentTheme(themedStyles);

    const active = !palette.descendant && !isAnimatingIn;

    /*
     * Focus the input when changing the palette.
     */
    React.useEffect(() => {
        if (palette.descendant) {
            // Descendant will focus its own input
            return;
        }

        // @ts-ignore
        const element = inputRef.current as HTMLInputElement;

        if (element) {
            const isInputFocused = document.activeElement === element;
            if (!isInputFocused) {
                element.select();
                element.focus();
            }
        }
    }, [palette]);

    return (
        <TouchableWithoutFeedback onPress={(event) => event.preventDefault()}>
            <ResponsiveBackgroundView
                ref={ref}
                backgroundColor={theme.colors.background.main}
                style={[styles.palette]}
                mobileStyle={styles.paletteMobile}
                desktopStyle={[styles.paletteInline, positionStyle]}
                onStartShouldSetResponder={() => true}
            >
                {hasPaletteSearchInput(palette) ? (
                    <PaletteQueryInput
                        ref={inputRef}
                        palette={palette}
                        setPalette={setPalette}
                        withItems={computed.items.length > 0}
                        placement={placement}
                        onTab={() => {
                            firstButtonRef.current?.focus();
                        }}
                    />
                ) : null}
                {palette.heading ? (
                    <div className="mx-4 mt-4 flex items-center text-ui-heading-small">
                        {palette.heading}
                    </div>
                ) : null}
                {computed.error ? (
                    <PaletteErrorMessage error={computed.error} />
                ) : (
                    <PaletteCommandsList
                        computed={computed}
                        palette={palette}
                        withKeyboardInteractions={active}
                        isAnimatingIn={isAnimatingIn}
                        setPalette={setPalette}
                        setTopPalette={setTopPalette}
                    />
                )}
                {palette.footerText ||
                (palette.footerButtons && palette.footerButtons.length > 0) ? (
                    <div className="flex items-center justify-between gap-2 border-t border-t-base px-3 py-2">
                        <div className="text-muted text-ui-small">{palette.footerText}</div>
                        {palette.footerButtons ? (
                            <div className="flex items-center justify-end gap-2">
                                {palette.footerButtons.map((button, index) => (
                                    <PaletteButton
                                        key={button.key}
                                        ref={index === 0 ? firstButtonRef : undefined}
                                        button={button}
                                        setPaletteState={setPalette}
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </ResponsiveBackgroundView>
        </TouchableWithoutFeedback>
    );
});

const PaletteButton = React.forwardRef(function PaletteButton(
    props: {
        button: PaletteButtonDef;
        setPaletteState: SetterOrUpdater<PaletteDef | null>;
    },
    ref: React.Ref<HTMLButtonElement>
) {
    const { button, setPaletteState } = props;

    return (
        <Button
            ref={ref}
            size="medium"
            label={button.label}
            leadingIcon={button.leadingIcon}
            kind={button.kind}
            disabled={button.disabled}
            onClick={async () => {
                await button.onClick();
                setPaletteState(null);
            }}
        />
    );
});

const themedStyles = ThemedStyleSheet.create({
    palette: (theme) => ({
        borderRadius: theme.radiuses.xlarge,
        borderColor: 'var(--sp-color-border-base)',
        borderWidth: 1,
        overflow: 'hidden',
        ...theme.shadows.pop,
    }),
    paletteMobile: {
        position: 'absolute',
        marginTop: 0,
        top: spacing.small,
        left: spacing.small,
        right: spacing.small,
        maxHeight: 440,
        width: 'auto',
        maxWidth: '100%',
        minWidth: 'auto',
    },
    paletteInline: {
        position: 'absolute',
    },
});
